/**
 * Màn **Sản phẩm** — catalogue thật của Website IME, nhóm theo site, và nút đồng bộ chủ động.
 *
 * Lynust chốt 10/09/2026: **mỗi site một nút, cộng một nút "tất cả"**; bấm thì **chờ tại chỗ**
 * và hiện kết quả ngay, không đẩy sang job chạy nền.
 *
 * Ba chỗ màn này cố ý không nói dối:
 *
 * 1. **`company` và `miradry` không có catalogue** — đó là **đúng thiết kế** bên Website IME.
 *    Chúng có hàng riêng nói ra điều đó, chứ không phải một nút bấm vào là hỏng, cũng không phải
 *    một hàng `0` trông như dữ liệu chưa nạp.
 * 2. **`dongBoLuc === null` là "chưa đồng bộ lần nào"**, hiện gạch chứ không hiện ngày giả.
 * 3. **Sản phẩm đã gỡ khỏi IME vẫn ở lại**, đánh dấu `ngừng dùng`. Nó không sinh bài mới nữa
 *    nhưng bài cũ còn truy được nguồn.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type KetQuaDongBo, type TomTatSanPham } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { gioVN } from "../lib/nhan.ts";
import { useToast } from "../thanh-phan/toast.tsx";
import {
  Bao,
  BocBang,
  Chip,
  ChuaDo,
  DangTai,
  DauBang,
  DauThe,
  DauTrang,
  Nut,
  OBang,
  OTieuDe,
  Rong,
  So,
  ThanBang,
  HangNut,
  The,
  ViSao,
} from "../thanh-phan/giao-dien.tsx";

/** Câu tóm tắt một lượt đồng bộ, để đẩy vào toast. Không có site nào đọc được thì nói thẳng. */
function tomTatLuot(kq: KetQuaDongBo): string {
  const doc = kq.site.filter((s) => s.coCatalogue && s.docDuoc);
  if (doc.length === 0) return "Không đọc được database nào.";
  const cong = (f: (s: (typeof doc)[number]) => number) => doc.reduce((a, s) => a + f(s), 0);
  const phan = [
    `${cong((s) => s.moi)} mới`,
    `${cong((s) => s.capNhat)} cập nhật`,
    `${cong((s) => s.ngungDung)} ngừng dùng`,
  ];
  const songLai = cong((s) => s.songLai);
  if (songLai > 0) phan.push(`${songLai} sống lại`);
  return `${phan.join(", ")} — ${kq.giay}s`;
}

export function SanPham() {
  const kho = useQueryClient();
  const toast = useToast();
  const [dangXem, datDangXem] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: khoa.sanPham,
    queryFn: api.sanPhamTomTat,
  });

  const dongBo = useMutation({
    mutationFn: (site?: string) => api.dongBoCatalogue(site),
    onSuccess: (kq, site) => {
      const hong = kq.site.filter((s) => s.coCatalogue && !s.docDuoc);
      if (hong.length > 0) {
        // Một site hỏng KHÔNG được lẫn vào toast xanh. Lượt vẫn chạy cho các site khác, nhưng
        // câu người đọc mang đi phải là câu về chỗ hỏng.
        toast.loi(
          `Không đọc được ${hong.map((s) => s.site).join(", ")}`,
          hong[0]?.canLam ?? hong[0]?.loi ?? undefined,
        );
      } else {
        toast.on(site ? `Đồng bộ ${site} xong` : "Đồng bộ xong", tomTatLuot(kq));
      }
      void kho.invalidateQueries({ queryKey: khoa.sanPham });
      if (dangXem) void kho.invalidateQueries({ queryKey: khoa.sanPhamCuaSite(dangXem) });
    },
    onError: (loi: Error) => toast.loi("Không đồng bộ được", loi.message),
  });

  if (error) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Sản phẩm" />
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
        </Bao>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Sản phẩm" />
        <DangTai />
      </div>
    );
  }

  const coCatalogue = data.site.filter((s) => s.coCatalogue);
  const chuaDongBoBaoGio = coCatalogue.filter((s) => s.dongBoLuc === null);
  const tongKhongDangDuoc = coCatalogue.reduce((a, s) => a + s.anhKhongDangDuoc, 0);

  return (
    <div className="space-y-4">
      <DauTrang
        tieuDe="Sản phẩm"
        moTa="Catalogue thật đọc từ Website IME. AutoIME chỉ đọc, không ghi gì sang đó."
      />

      <HangNut>
        <Nut
          kieu="chinh"
          dangChay={dongBo.isPending && dongBo.variables === undefined}
          disabled={dongBo.isPending}
          onClick={() => dongBo.mutate(undefined)}
        >
          Đồng bộ tất cả
        </Nut>
      </HangNut>

      {chuaDongBoBaoGio.length > 0 && (
        <Bao muc="canh" tieuDe={`${chuaDongBoBaoGio.length} site chưa đồng bộ lần nào`}>
          {chuaDongBoBaoGio.map((s) => s.site).join(", ")} — số sản phẩm hiện có là kết quả của một
          lần nạp bằng lệnh, không phải của nút này. Bấm đồng bộ để có mốc thời gian thật.
        </Bao>
      )}

      {tongKhongDangDuoc > 0 && (
        <Bao muc="loi" tieuDe={`${tongKhongDangDuoc} ảnh KHÔNG đăng được`}>
          Bài dùng chúng bị chặn <strong>trước khi gửi</strong>. Nguyên nhân gần như luôn là
          catalogue được nạp từ Mongo của máy dev (ảnh trỏ <code>localhost:8081</code>) chứ không
          phải từ production.
        </Bao>
      )}

      <The>
        <DauThe tieuDe="Năm site" phu="Ba site có catalogue; hai site còn lại không, và đó là đúng thiết kế." />
        <BocBang rongToiThieu="52rem">
          <table className="w-full text-sm">
            <DauBang>
              <tr>
                <OTieuDe>Site</OTieuDe>
                <OTieuDe canh="phai">Đang bán</OTieuDe>
                <OTieuDe canh="phai">Ngừng dùng</OTieuDe>
                <OTieuDe canh="phai">Ảnh đăng được</OTieuDe>
                <OTieuDe canh="phai">Ảnh KHÔNG</OTieuDe>
                <OTieuDe>Đồng bộ lúc</OTieuDe>
                <OTieuDe canh="phai">{""}</OTieuDe>
              </tr>
            </DauBang>
            <ThanBang>
              {data.site.map((s) => (
                <HangSite
                  key={s.site}
                  s={s}
                  dangChay={dongBo.isPending && dongBo.variables === s.site}
                  khoaHet={dongBo.isPending}
                  dangXem={dangXem === s.site}
                  onDongBo={() => dongBo.mutate(s.site)}
                  onXem={() => datDangXem(dangXem === s.site ? null : s.site)}
                />
              ))}
            </ThanBang>
          </table>
        </BocBang>

        <ViSao>
          <strong>Đang bán</strong> là số món còn thấy bên Website IME. Món bị gỡ chuyển sang{" "}
          <strong>ngừng dùng</strong> chứ không bị xoá: xoá hẳn thì bài đã đăng trỏ tới một mục
          không còn tồn tại, và câu hỏi <em>"bài này viết về món nào"</em> mất câu trả lời vĩnh
          viễn. Món ngừng dùng <strong>không</strong> được chọn để viết bài mới.
        </ViSao>
      </The>

      {dangXem && <DanhSachSite site={dangXem} />}
    </div>
  );
}

function HangSite({
  s,
  dangChay,
  khoaHet,
  dangXem,
  onDongBo,
  onXem,
}: {
  s: TomTatSanPham;
  dangChay: boolean;
  khoaHet: boolean;
  dangXem: boolean;
  onDongBo: () => void;
  onXem: () => void;
}) {
  if (!s.coCatalogue) {
    return (
      <tr className="text-muc-500">
        <OBang className="font-medium text-muc-700">{s.site}</OBang>
        <td className="px-3 py-2 text-sm" colSpan={6}>
          Không có catalogue — <strong>đúng thiết kế</strong> bên Website IME. Site này lấy đề tài
          từ nghiên cứu ngoài, không từ sản phẩm.
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <OBang>
        <button
          type="button"
          onClick={onXem}
          className="font-medium text-muc-800 underline decoration-muc-300 underline-offset-2 hover:decoration-muc-600"
          aria-expanded={dangXem}
        >
          {s.site}
        </button>
      </OBang>
      <So sacThai={s.conBenIme === 0 ? "xau" : undefined}>{s.conBenIme}</So>
      <So sacThai={s.ngungDung > 0 ? "nhan" : "mo"}>{s.ngungDung}</So>
      <So sacThai="tot">{s.anhDangDuoc}</So>
      <So sacThai={s.anhKhongDangDuoc > 0 ? "xau" : "mo"}>{s.anhKhongDangDuoc}</So>
      <OBang className="whitespace-nowrap text-muc-600">
        {s.dongBoLuc === null ? <ChuaDo nhan="chưa đồng bộ lần nào" /> : gioVN(s.dongBoLuc)}
      </OBang>
      <OBang className="text-right">
        <Nut dangChay={dangChay} disabled={khoaHet} onClick={onDongBo}>
          Đồng bộ
        </Nut>
      </OBang>
    </tr>
  );
}

function DanhSachSite({ site }: { site: string }) {
  const { data, isPending, error } = useQuery({
    queryKey: khoa.sanPhamCuaSite(site),
    queryFn: () => api.sanPhamCuaSite(site),
  });

  if (error) {
    return (
      <Bao muc="loi" tieuDe={`Không tải được sản phẩm của ${site}`}>
        {error.message}
      </Bao>
    );
  }
  if (isPending) return <DangTai />;
  if (data.muc.length === 0) {
    return (
      <Rong tieuDe={`Catalogue của ${site} rỗng`}>
        Bấm <strong>Đồng bộ</strong> ở hàng {site} để nạp từ Website IME.
      </Rong>
    );
  }

  return (
    <The>
      <DauThe
        tieuDe={`${site} — ${data.muc.length} sản phẩm`}
        phu="Món còn bán đứng trước; trong đó món lâu chưa dùng nhất lên đầu — đó cũng là thứ tự chọn để viết bài."
      />
      <BocBang rongToiThieu="52rem">
        <table className="w-full text-sm">
          <DauBang>
            <tr>
              <OTieuDe>{""}</OTieuDe>
              <OTieuDe>Tên sản phẩm</OTieuDe>
              <OTieuDe canh="phai">Ảnh</OTieuDe>
              <OTieuDe canh="phai">Đăng được</OTieuDe>
              <OTieuDe canh="phai">Đã dùng</OTieuDe>
              <OTieuDe>Lần cuối</OTieuDe>
            </tr>
          </DauBang>
          <ThanBang>
            {data.muc.map((m) => (
              <tr key={m.id} className={m.conBenIme ? undefined : "opacity-60"}>
                <OBang className="w-14">
                  {m.anhDaiDien ? (
                    <img
                      src={m.anhDaiDien}
                      alt=""
                      loading="lazy"
                      className="h-10 w-10 rounded-tile border border-muc-200 object-cover"
                    />
                  ) : (
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-tile border border-dashed border-muc-300 text-[10px] text-muc-400"
                      title="Không có ảnh nào đăng được"
                    >
                      —
                    </div>
                  )}
                </OBang>
                <OBang>
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-muc-800" title={m.tenSanPham}>
                      {m.tenSanPham}
                    </span>
                    {!m.conBenIme && (
                      <Chip muc="canh" title={m.ngungDungLuc ? `Phát hiện ${gioVN(m.ngungDungLuc)}` : undefined}>
                        ngừng dùng
                      </Chip>
                    )}
                  </div>
                </OBang>
                <So sacThai="mo">{m.soAnh}</So>
                <So sacThai={m.anhDangDuoc === 0 ? "xau" : "tot"}>{m.anhDangDuoc}</So>
                <So sacThai={m.soLanDaDung === 0 ? "mo" : undefined}>{m.soLanDaDung}</So>
                <OBang className="whitespace-nowrap text-muc-600">
                  {m.lanCuoiDungLuc === null ? (
                    <ChuaDo nhan="chưa dùng lần nào" />
                  ) : (
                    gioVN(m.lanCuoiDungLuc)
                  )}
                </OBang>
              </tr>
            ))}
          </ThanBang>
        </table>
      </BocBang>
    </The>
  );
}
